/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type GitHubClient from './GitHubClient';
import type {PullRequest} from './pullRequestTimelineTypes';
import type {PullsQueryInput, PullsWithPageInfo} from './pullsTypes';
import type {CommitComparison, RestPullRequestFile} from './restApiTypes';
import type {Blob, Commit, GitObject, GitObjectID, ID, Tree} from './types';
import type {
  AddCommentMutationData,
  AddLabelsToLabelableInput,
  AddLabelsToLabelableMutationData,
  AddPullRequestReviewInput,
  AddPullRequestReviewMutationData,
  AddPullRequestReviewCommentInput,
  AddPullRequestReviewCommentMutationData,
  AddPullRequestReviewThreadInput,
  AddPullRequestReviewThreadMutationData,
  ClosePullRequestMutationData,
  LabelFragment,
  MergePullRequestInput,
  MergeableState,
  MergePullRequestMutationData,
  UpdatePullRequestBaseMutationData,
  PullRequestReviewDecision,
  PullRequestState,
  RemoveLabelsFromLabelableInput,
  RemoveLabelsFromLabelableMutationData,
  RequestReviewsInput,
  RequestReviewsMutationData,
  ResolveReviewThreadMutationData,
  StackPullRequestFragment,
  SubmitPullRequestReviewInput,
  SubmitPullRequestReviewMutationData,
  DeletePullRequestReviewCommentInput,
  DeletePullRequestReviewCommentMutationData,
  UnresolveReviewThreadMutationData,
  UpdatePullRequestReviewCommentInput,
  UpdatePullRequestReviewCommentMutationData,
  UserFragment,
} from '../generated/graphql';

import {globalCacheStats} from './GitHubClientStats';
import {DB_VERSION, DB_NAME} from './databaseInfo';
import {subscribeToLogout} from './logoutBroadcastChannel';
import rejectAfterTimeout from 'shared/rejectAfterTimeout';

const DB_COMMIT_STORE_NAME = 'commit';
const DB_TREE_STORE_NAME = 'tree';
const DB_BLOB_STORE_NAME = 'blob';
const PR_FRAGMENT_STORE_NAME = 'pr-fragment';

/**
 * Maximum number of entries allowed per object store before eviction kicks in.
 * Blobs are the largest (full file contents), so they get the smallest limit.
 * When a store exceeds its limit, it is cleared entirely — the cache is a
 * performance optimization, not a source of truth, so this is safe.
 */
const MAX_STORE_ENTRIES: Record<string, number> = {
  [DB_BLOB_STORE_NAME]: 2000,
  [DB_TREE_STORE_NAME]: 3000,
  [DB_COMMIT_STORE_NAME]: 1000,
  // PR fragments are tiny; keep more.
  [PR_FRAGMENT_STORE_NAME]: 5000,
};

// =============================================================================
// Batched IndexedDB Reader
// =============================================================================
//
// When 30+ files activate simultaneously, each blob read creates its own
// IndexedDB transaction. IndexedDB transactions have significant per-transaction
// overhead (lock acquisition, I/O scheduling), so 60+ concurrent transactions
// on the same store create a bottleneck.
//
// BatchedStoreReader coalesces all reads arriving within the same microtask
// into a single IndexedDB transaction, turning 60 transactions into 1.

type PendingRead<T> = {
  resolve: (value: T | null) => void;
  reject: (error: unknown) => void;
};

class BatchedStoreReader<T> {
  private pendingByKey = new Map<string, Array<PendingRead<T>>>();
  private flushScheduled = false;
  /** In-memory cache: avoids IndexedDB entirely for repeated reads. */
  private memoryCache = new Map<string, T | null>();
  /**
   * MessageChannel used to schedule the flush as a macrotask. Unlike
   * queueMicrotask (which fires between microtasks), a MessageChannel task
   * fires AFTER the entire microtask queue drains. This is critical because
   * Jotai atom evaluations chain through multiple awaits (e.g.,
   * `await get(gitHubClientAtom)` in every gitHubBlobAtom), spreading reads
   * across many microtasks. By flushing as a macrotask, we guarantee all
   * reads from the current event loop turn — regardless of how many await
   * hops they traverse — are coalesced into a single IndexedDB transaction.
   */
  private flushChannel: MessageChannel;

  constructor(
    private db: IDBDatabase,
    private storeName: string,
  ) {
    this.flushChannel = new MessageChannel();
    this.flushChannel.port1.onmessage = () => this.flush();
  }

  get(key: string): Promise<T | null> {
    // Fast path: already in memory from a previous read.
    const cached = this.memoryCache.get(key);
    if (cached !== undefined) {
      this.memoryHits++;
      return Promise.resolve(cached);
    }

    return new Promise<T | null>((resolve, reject) => {
      const existing = this.pendingByKey.get(key);
      if (existing != null) {
        // Another read for the same key is already pending — just piggyback.
        existing.push({resolve, reject});
      } else {
        this.pendingByKey.set(key, [{resolve, reject}]);
      }

      if (!this.flushScheduled) {
        this.flushScheduled = true;
        // Post a message to schedule flush as a macrotask — fires after all
        // microtasks drain, giving maximum coalescing window.
        this.flushChannel.port2.postMessage(null);
      }
    });
  }

  /** Populate the memory cache after a network-fetched blob is written to IDB. */
  cacheInMemory(key: string, value: T | null): void {
    this.memoryCache.set(key, value);
  }

  private flush(): void {
    this.flushScheduled = false;

    const batch = this.pendingByKey;
    this.pendingByKey = new Map();

    if (batch.size === 0) {
      return;
    }

    const t0 = performance.now();
    const batchSize = batch.size;
    let resolved = 0;
    const memHits = this.memoryHits;
    this.memoryHits = 0;

    const tx = this.db.transaction(this.storeName, 'readonly');
    const store = tx.objectStore(this.storeName);

    for (const [key, callbacks] of batch) {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = (request.result as T) ?? null;
        this.memoryCache.set(key, result);
        for (const {resolve} of callbacks) {
          resolve(result);
        }
        resolved++;
        if (resolved === batchSize) {
          const elapsed = Math.round(performance.now() - t0);
          // eslint-disable-next-line no-console
          console.log(
            `[BatchedStoreReader:${this.storeName}] batch=${batchSize} memHits=${memHits} idb=${elapsed}ms`,
          );
        }
      };
      request.onerror = () => {
        for (const {reject} of callbacks) {
          reject(request.error);
        }
      };
    }
  }

  /** Track memory cache hits between flushes for logging. */
  private memoryHits = 0;
}

interface NormalizedCommit extends GitObject {
  oid: GitObjectID;
  id: ID;
  url: string;
  message: string;
  messageHeadline: string;
  messageHeadlineHTML: string;
  messageBody: string;
  messageBodyHTML: string;
  parents: GitObjectID[];
  tree_oid: GitObjectID;
  committedDate: string;
}

type NormalizedStackPullRequestFragment = {
  owner: string;
  name: string;
  id: string;
  number: number;
  title: string;
  updatedAt: string;
  state: PullRequestState;
  reviewDecision: PullRequestReviewDecision | null | undefined;
  baseRefName: string;
  headRefName: string;
  headRefOid: GitObjectID;
  mergeable: MergeableState;
  lastCommentBody: string | null;
  numComments: number;
  numReviewThreads: number;
};

/** Name of an IDBObjectStore in our IDBDatabase. */
type Store =
  | typeof DB_COMMIT_STORE_NAME
  | typeof DB_TREE_STORE_NAME
  | typeof DB_BLOB_STORE_NAME
  | typeof PR_FRAGMENT_STORE_NAME;

type StoreTypes = {
  [DB_COMMIT_STORE_NAME]: NormalizedCommit;
  [DB_TREE_STORE_NAME]: Tree;
  [DB_BLOB_STORE_NAME]: Blob;
  [PR_FRAGMENT_STORE_NAME]: NormalizedStackPullRequestFragment;
};

/**
 * Represents an open readwrite transaction for the specified store.
 * Callers are expected to invoke add() as many times as necessary
 * (awaiting the result if they want confirmation that the IDBRequest for the
 * corresponding add() call succeeded) and finally invoking commit() when
 * finished.
 */
class OpenTransaction<S extends Store, O = StoreTypes[S]> {
  private tx: IDBTransaction;
  private store: IDBObjectStore;
  private txResult: Promise<void>;

  constructor(db: IDBDatabase, private storeName: S) {
    this.tx = db.transaction(storeName, 'readwrite');
    this.store = this.tx.objectStore(storeName);
    this.txResult = new Promise((resolve, reject) => {
      this.tx.oncomplete = () => resolve();
      this.tx.onerror = event => {
        if ((event?.target as IDBRequest).error?.name === 'ConstraintError') {
          switch (this.storeName) {
            case DB_BLOB_STORE_NAME: {
              ++globalCacheStats.duplicateKeyBlob;
              break;
            }
            case DB_TREE_STORE_NAME: {
              ++globalCacheStats.duplicateKeyTree;
              break;
            }
            case DB_COMMIT_STORE_NAME: {
              ++globalCacheStats.duplicateKeyCommit;
              break;
            }
          }
          resolve();
        } else {
          reject();
        }
      };
    });
  }

  add(obj: O): Promise<void> {
    return new Promise((resolve, reject) => {
      let request;
      try {
        request = this.store.add(obj);
      } catch (e) {
        return reject(e);
      }

      request.onsuccess = _event => resolve();
      request.onerror = event => {
        if ((event?.target as IDBRequest).error?.name === 'ConstraintError') {
          let identifier = 'unknown';
          if (implementsGitObject(obj)) {
            identifier = obj.oid;
          } else {
            const pr = obj as unknown as NormalizedStackPullRequestFragment;
            identifier = `${pr.owner}/${pr.name}/${pr.number}`;
          }
          // eslint-disable-next-line no-console
          console.info(`${identifier} already added to store ${this.store.name}`);
          resolve();
        } else {
          reject(event);
        }
      };
    });
  }

  /** Like add(), but overwrites existing records instead of throwing ConstraintError. */
  put(obj: O): Promise<void> {
    return new Promise((resolve, reject) => {
      let request;
      try {
        request = this.store.put(obj);
      } catch (e) {
        return reject(e);
      }
      request.onsuccess = _event => resolve();
      request.onerror = event => reject(event);
    });
  }

  /**
   * Returns a Promise that resolves when the underlying IDBTransaction
   * completes.
   */
  commit(): Promise<void> {
    this.tx.commit();
    return this.txResult;
  }
}

function implementsGitObject(obj: unknown): obj is GitObject {
  return typeof obj === 'object' && obj !== null && 'oid' in obj;
}

/**
 * Decorates a GitHubClient, but uses IndexedDB as a caching layer.
 */
export default class CachingGitHubClient implements GitHubClient {
  private blobReader: BatchedStoreReader<Blob>;

  /**
   * owner and name must be non-null if the getStackPullRequests() will be
   * used.
   */
  constructor(
    private db: IDBDatabase,
    private client: GitHubClient,
    private owner: string | null,
    private name: string | null,
  ) {
    this.blobReader = new BatchedStoreReader<Blob>(db, DB_BLOB_STORE_NAME);
  }

  async getCommit(oid: GitObjectID): Promise<Commit | null> {
    const cachedCommit = await this.getCachedCommit(oid);
    if (cachedCommit != null) {
      ++globalCacheStats.cacheCommitReads;
      return cachedCommit;
    }

    const commit = await this.client.getCommit(oid);
    if (commit != null) {
      // Note that multiple commits may have the same root Tree object (this is
      // particularly common with stacks created via ghstack), in which case the
      // underlying IDBTransaction will fail with a ConstraintError if the Tree
      // already exists in IndexedDB as a result of persisting some other
      // Commit. (Note that OpenTransaction will swallow this error so the
      // Promise returned by add() will not reject.)
      //
      // There is no great way to avoid this without incurring the cost of an
      // extra read, which hardly seems worth the cost and is not 100% reliable
      // because it is subject to TOCTOU races. For the curious,
      // GitHubClientStats is available to see how often this happens, in
      // practice.
      //
      // Further, we choose not to await the calls to tx.add() (or even invoke
      // tx.commit()) and return to the caller before the Tree or Commit is
      // persisted to IndexedDB. This knowingly runs the risk of fetching the
      // Tree or Commit multiple times.
      {
        const tx = new OpenTransaction(this.db, DB_TREE_STORE_NAME);
        tx.add(commit.tree);
      }
      {
        const tx = new OpenTransaction(this.db, DB_COMMIT_STORE_NAME);
        const normalizedCommit = normalizeCommit(commit);
        tx.add(normalizedCommit);
      }
    }
    return commit;
  }

  getCommitComparison(base: GitObjectID, head: GitObjectID): Promise<CommitComparison | null> {
    // No caching done for now.
    return this.client.getCommitComparison(base, head);
  }

  async getTree(oid: GitObjectID): Promise<Tree | null> {
    const cachedTree = await this.getCachedTree(oid);
    if (cachedTree != null) {
      ++globalCacheStats.cacheTreeReads;
      return cachedTree;
    }

    const tree = await this.client.getTree(oid);
    // Note that if tree is null, it is possible that a Tree with the
    // GitObjectID comes into existence later, so we should not write a null
    // entry to IndexedDB.
    if (tree != null) {
      // We choose not to await the call to tx.add() (or even invoke
      // tx.commit()) and return to the caller before the Tree is persisted to
      // IndexedDB. This knowingly runs the risk of fetching the Tree multiple
      // times.
      const tx = new OpenTransaction(this.db, DB_TREE_STORE_NAME);
      tx.add(tree);
    }
    return tree;
  }

  async getBlob(oid: GitObjectID): Promise<Blob | null> {
    const cachedBlob = await this.blobReader.get(oid);
    if (cachedBlob != null) {
      ++globalCacheStats.cacheBlobReads;
      return cachedBlob;
    }

    const blob = await this.client.getBlob(oid);
    // Note that if blob is null, it is possible that a Blob with the
    // GitObjectID comes into existence later, so we should not write a null
    // entry to IndexedDB.
    if (blob != null) {
      // It is imperative that the blob be persisted to IndexedDB before
      // returning because diffServiceWorker assumes that if it receives a
      // GitObjectID for a Blob, it will be able to read it from IndexedDB.
      const tx = new OpenTransaction(this.db, DB_BLOB_STORE_NAME);
      await tx.add(blob);
      await tx.commit();
      // Also populate the in-memory cache so subsequent reads skip IndexedDB.
      this.blobReader.cacheInMemory(oid, blob);
    }
    return blob;
  }

  getPullRequest(pr: number): Promise<PullRequest | null> {
    // No caching done because the PR could have been updated since the PR data
    // were requested last.
    return this.client.getPullRequest(pr);
  }

  getPullRequests(input: PullsQueryInput): Promise<PullsWithPageInfo | null> {
    return this.client.getPullRequests(input);
  }

  getRepoAssignableUsers(query: string | null): Promise<UserFragment[]> {
    return this.client.getRepoAssignableUsers(query);
  }

  getRepoLabels(query: string | null): Promise<LabelFragment[]> {
    return this.client.getRepoLabels(query);
  }

  async getStackPullRequests(prs: number[]): Promise<StackPullRequestFragment[]> {
    // Always fetch fresh from GitHub — PR fragments contain mutable fields
    // (reviewDecision, state, title, updatedAt, comment counts) that change
    // frequently. Serving stale data from IndexedDB causes the stack dropdown
    // to show outdated status badges.
    const fragments = await this.client.getStackPullRequests(prs);

    // Write fetched fragments to IndexedDB so they're available for offline
    // or for other consumers that may read directly from the cache.
    const {owner, name} = this.getOwnerAndName();
    const tx = new OpenTransaction(this.db, PR_FRAGMENT_STORE_NAME);
    await Promise.all(
      fragments.map(fragment => {
        const normalizedFragment = normalizePullRequestFragment(owner, name, fragment);
        return tx.put(normalizedFragment);
      }),
    );
    await tx.commit();
    return fragments;
  }

  findPullRequestsByBranch(params: {
    headRefName?: string;
    baseRefName?: string;
  }): Promise<Array<{number: number; headRefName: string; baseRefName: string}>> {
    // No caching — this is a lightweight discovery query that should always be fresh
    return this.client.findPullRequestsByBranch(params);
  }

  addComment(id: ID, body: string): Promise<AddCommentMutationData> {
    return this.client.addComment(id, body);
  }

  addLabels(input: AddLabelsToLabelableInput): Promise<AddLabelsToLabelableMutationData> {
    return this.client.addLabels(input);
  }

  addPullRequestReview(
    input: AddPullRequestReviewInput,
  ): Promise<AddPullRequestReviewMutationData> {
    return this.client.addPullRequestReview(input);
  }

  addPullRequestReviewComment(
    input: AddPullRequestReviewCommentInput,
  ): Promise<AddPullRequestReviewCommentMutationData> {
    return this.client.addPullRequestReviewComment(input);
  }

  addPullRequestReviewThread(
    input: AddPullRequestReviewThreadInput,
  ): Promise<AddPullRequestReviewThreadMutationData> {
    return this.client.addPullRequestReviewThread(input);
  }

  removeLabels(
    input: RemoveLabelsFromLabelableInput,
  ): Promise<RemoveLabelsFromLabelableMutationData> {
    return this.client.removeLabels(input);
  }

  requestReviews(input: RequestReviewsInput): Promise<RequestReviewsMutationData> {
    return this.client.requestReviews(input);
  }

  submitPullRequestReview(
    input: SubmitPullRequestReviewInput,
  ): Promise<SubmitPullRequestReviewMutationData> {
    return this.client.submitPullRequestReview(input);
  }

  resolveReviewThread(threadId: ID): Promise<ResolveReviewThreadMutationData> {
    return this.client.resolveReviewThread(threadId);
  }

  unresolveReviewThread(threadId: ID): Promise<UnresolveReviewThreadMutationData> {
    return this.client.unresolveReviewThread(threadId);
  }

  mergePullRequest(input: MergePullRequestInput): Promise<MergePullRequestMutationData> {
    return this.client.mergePullRequest(input);
  }

  closePullRequest(pullRequestId: ID): Promise<ClosePullRequestMutationData> {
    return this.client.closePullRequest(pullRequestId);
  }

  updatePullRequestReviewComment(
    input: UpdatePullRequestReviewCommentInput,
  ): Promise<UpdatePullRequestReviewCommentMutationData> {
    return this.client.updatePullRequestReviewComment(input);
  }

  deletePullRequestReviewComment(
    input: DeletePullRequestReviewCommentInput,
  ): Promise<DeletePullRequestReviewCommentMutationData> {
    return this.client.deletePullRequestReviewComment(input);
  }

  updatePullRequestBase(pullRequestId: ID, baseRefName: string): Promise<UpdatePullRequestBaseMutationData> {
    return this.client.updatePullRequestBase(pullRequestId, baseRefName);
  }

  getPullRequestState(pr: number): Promise<import('./GitHubClient').PullRequestFreshState | null> {
    return this.client.getPullRequestState(pr);
  }

  getDefaultBranch(): Promise<string> {
    return this.client.getDefaultBranch();
  }

  markFileAsViewed(pullRequestId: ID, path: string): Promise<import('../generated/graphql').MarkFileAsViewedMutationData> {
    return this.client.markFileAsViewed(pullRequestId, path);
  }

  unmarkFileAsViewed(pullRequestId: ID, path: string): Promise<import('../generated/graphql').UnmarkFileAsViewedMutationData> {
    return this.client.unmarkFileAsViewed(pullRequestId, path);
  }

  getPullRequestFiles(pr: number): Promise<RestPullRequestFile[]> {
    return this.client.getPullRequestFiles(pr);
  }

  deleteBranch(branchName: string): Promise<void> {
    return this.client.deleteBranch(branchName);
  }

  /**
   * Attempts to fetch the commit from the local IndexedDB. Returns null if
   * the commit could not be found in IndexedDB, though it could still exist on
   * the server.
   */
  private getCachedCommit(oid: GitObjectID): Promise<Commit | null> {
    const tx = this.db.transaction(DB_COMMIT_STORE_NAME, 'readonly');
    const store = tx.objectStore(DB_COMMIT_STORE_NAME);
    const request = store.get(oid);
    return new Promise((resolve, reject) => {
      request.onsuccess = async _event => {
        const {result: normalizedCommit} = request;
        if (normalizedCommit == null) {
          return resolve(null);
        }

        const {
          oid,
          id,
          url,
          message,
          messageHeadline,
          messageHeadlineHTML,
          messageBody,
          messageBodyHTML,
          parents,
          tree_oid,
          committedDate,
        } = normalizedCommit;
        const tree = await this.getTree(tree_oid);
        if (tree == null) {
          return reject(`tree ${tree_oid} not found for commit ${oid}`);
        }

        const commit = {
          oid: oid as GitObjectID,
          id: id as ID,
          url,
          message: message as string,
          messageHeadline: messageHeadline as string,
          messageHeadlineHTML,
          messageBody,
          messageBodyHTML,
          parents: parents as GitObjectID[],
          tree,
          committedDate,
        };
        resolve(commit);
      };
      request.onerror = reject;
    });
  }

  /**
   * Attempts to fetch the tree from the local IndexedDB. Returns null if
   * the tree could not be found in IndexedDB, though it could still exist on
   * the server.
   */
  private getCachedTree(oid: GitObjectID): Promise<Tree | null> {
    const tx = this.db.transaction(DB_TREE_STORE_NAME, 'readonly');
    const store = tx.objectStore(DB_TREE_STORE_NAME);
    const request = store.get(oid);
    return new Promise((resolve, reject) => {
      request.onsuccess = _event => {
        resolve(request.result ?? null);
      };
      request.onerror = reject;
    });
  }


  private getOwnerAndName(): {owner: string; name: string} {
    const {owner, name} = this;
    if (owner == null || name == null) {
      throw new Error('owner and name must be set in CachingGitHubClient');
    }
    return {owner, name};
  }
}

const OPEN_DATABASE_TIMEOUT_MS = 10 * 1000;

/**
 * Returns an open connection to an IDBDatabase that will close if the user
 * logs out (from any window on this origin in the same browser).
 */
export function openDatabase(): Promise<IDBDatabase> {
  return _openDatabase().then(db => {
    // If we get a "logout" event from another window, close the connection in
    // this window so that the other window can call indexedDB.deleteDatabase()
    // on all the databases.
    subscribeToLogout(() => db.close(), /* includeLogoutEventsFromThisWindow */ true);

    // Run cache eviction asynchronously — don't block the caller.
    evictOversizedStores(db).catch(err =>
      // eslint-disable-next-line no-console
      console.warn('Cache eviction failed:', err),
    );

    return db;
  });
}

/**
 * Check each object store's entry count. If any store exceeds its limit,
 * clear it entirely. This keeps the IndexedDB size bounded so that
 * operations don't degrade over time.
 */
async function evictOversizedStores(db: IDBDatabase): Promise<void> {
  const storeNames = [
    DB_COMMIT_STORE_NAME,
    DB_TREE_STORE_NAME,
    DB_BLOB_STORE_NAME,
    PR_FRAGMENT_STORE_NAME,
  ] as const;

  for (const storeName of storeNames) {
    const count = await getStoreCount(db, storeName);
    const limit = MAX_STORE_ENTRIES[storeName];
    if (limit != null && count > limit) {
      // eslint-disable-next-line no-console
      console.info(
        `Cache eviction: ${storeName} has ${count} entries (limit ${limit}), clearing.`,
      );
      await clearStore(db, storeName);
    }
  }
}

function getStoreCount(db: IDBDatabase, storeName: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function clearStore(db: IDBDatabase, storeName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function _openDatabase(): Promise<IDBDatabase> {
  const openDatabaseRequest = new Promise((resolve, reject) => {
    const request = self.indexedDB.open(DB_NAME, DB_VERSION);
    request.onsuccess = _event => {
      resolve(request.result);
    };
    request.onerror = event => reject(event.target);
    request.onblocked = event =>
      // eslint-disable-next-line no-console
      console.error(
        `indexedDB blocked while trying to open ${DB_NAME} with version ${DB_VERSION}:`,
        event,
      );
    request.onupgradeneeded = (_event: IDBVersionChangeEvent) => {
      const {result: db} = request;
      const commitStore = db.createObjectStore(DB_COMMIT_STORE_NAME, {
        keyPath: 'oid',
        autoIncrement: false,
      });
      commitStore.createIndex('id', 'id', {unique: false});
      commitStore.createIndex('url', 'url', {unique: false});
      commitStore.createIndex('message', 'message', {unique: false});
      commitStore.createIndex('messageHeadline', 'messageHeadline', {unique: false});
      commitStore.createIndex('messageHeadlineHTML', 'messageHeadlineHTML', {unique: false});
      commitStore.createIndex('messageBody', 'messageBody', {unique: false});
      commitStore.createIndex('messageBodyHTML', 'messageBodyHTML', {unique: false});
      commitStore.createIndex('parents', 'parents', {unique: false});
      commitStore.createIndex('tree_oid', 'tree_oid', {unique: false});
      commitStore.createIndex('committedDate', 'committedDate', {unique: false});

      const treeStore = db.createObjectStore(DB_TREE_STORE_NAME, {
        keyPath: 'oid',
        autoIncrement: false,
      });
      treeStore.createIndex('id', 'id', {unique: false});
      treeStore.createIndex('entries', 'entries', {unique: false});

      const blobStore = db.createObjectStore(DB_BLOB_STORE_NAME, {
        keyPath: 'oid',
        autoIncrement: false,
      });
      blobStore.createIndex('id', 'id', {unique: false});
      blobStore.createIndex('byteSize', 'byteSize', {unique: false});
      blobStore.createIndex('isBinary', 'isBinary', {unique: false});
      blobStore.createIndex('isTruncated', 'isTruncated', {unique: false});
      blobStore.createIndex('text', 'text', {unique: false});

      const pullRequestFragmentStore = db.createObjectStore(PR_FRAGMENT_STORE_NAME, {
        keyPath: ['owner', 'name', 'number'],
        autoIncrement: false,
      });
      pullRequestFragmentStore.createIndex('title', 'title', {unique: false});
      pullRequestFragmentStore.createIndex('updatedAt', 'updatedAt', {unique: false});
      pullRequestFragmentStore.createIndex('state', 'state', {unique: false});
      pullRequestFragmentStore.createIndex('headRefOid', 'headRefOid', {unique: false});
      pullRequestFragmentStore.createIndex('numComments', 'numComments', {unique: false});
    };
  });

  // On one occasion, we saw indexedDB.open() fail to fire any of the
  // expected events (success, error, blocked, upgradeneeded) such that
  // openDatabaseRequest never settled, causing all queries into
  // CachingGitHubClient to hang forever. As a safeguard, we leverage
  // Promise.race() to introduce a timeout so force an explicit failure in this
  // case.
  //
  // Closing all of the Google Chrome browser tabs that were serving content
  // from the domain and then reopening them appeared to fix the issue.
  // Presumably there was some sort of active connection that was preventing
  // open calls from succeeding? Unclear.
  const database = await rejectAfterTimeout(
    openDatabaseRequest,
    OPEN_DATABASE_TIMEOUT_MS,
    `database failed to open within ${OPEN_DATABASE_TIMEOUT_MS}ms`,
  );
  if (database instanceof IDBDatabase) {
    return database;
  } else if (database instanceof Error) {
    throw database;
  } else {
    throw Error(`invariant failed, database object was: ${database}`);
  }
}

function normalizeCommit(commit: Commit): NormalizedCommit {
  const {
    oid,
    id,
    url,
    message,
    messageHeadline,
    messageHeadlineHTML,
    messageBody,
    messageBodyHTML,
    parents,
    committedDate,
  } = commit;
  return {
    oid,
    id,
    url,
    message,
    messageHeadline,
    messageHeadlineHTML,
    messageBody,
    messageBodyHTML,
    parents,
    tree_oid: commit.tree.oid,
    committedDate,
  };
}

function normalizePullRequestFragment(
  owner: string,
  name: string,
  fragment: StackPullRequestFragment,
): NormalizedStackPullRequestFragment {
  const {id, number, title, updatedAt, state, reviewDecision, baseRefName, headRefName, headRefOid, mergeable, comments, lastComment, reviewThreads} = fragment;
  return {
    owner,
    name,
    id,
    number,
    title,
    updatedAt,
    state,
    reviewDecision,
    baseRefName,
    headRefName,
    headRefOid,
    mergeable,
    lastCommentBody: lastComment.nodes?.[0]?.body ?? null,
    numComments: comments.totalCount,
    numReviewThreads: reviewThreads.totalCount,
  };
}
